import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useModal } from './ModalSystem';
import './LCIInputsTable.css';
import {
  getImpactScores, getImpactLink, hasImpactScores, isCostOnly, setImpactLink, impactSourceLabel, isCategoryCovered, DEFAULT_IMPACT_SOURCE,
  IMPACT_SOURCE_ORDER, GREET_SOURCE, impactSourceChoiceLabel, impactSourceLookupNote, IMPACT_SOURCE_CITATIONS,
} from '../services/impactSource';
import { withCost } from '../services/costProvenance';
import SourcesButton from './SourcesButton';

/** A price's citation for the sources popup: the source, its link, then the quote and note. */
function priceSources(input) {
  return {
    sources: [{ cite: input.cost_source, url: input.cost_url }],
    notes: [input.cost_quote && `\u201c${input.cost_quote}\u201d`, input.cost_note],
  };
}

/**
 * LCI Inputs Table — Template Mode
 *
 * Displays LCI inputs with baked-in TRACI scores and cost data.
 * No Brightway resolution — scores come directly from the template JSON.
 *
 * Props:
 *   processId   – current process ID (for keying)
 *   lciInputs   – array of LCI input objects from template
 *   onInputsChange – callback when inputs are modified (quantity edits, deletes)
 */
const LCIInputsTable = ({ processId, lciInputs = [], onInputsChange, useNoisyDb = false, impactSource = DEFAULT_IMPACT_SOURCE, onImpactSourceChange }) => {
  const { showConfirm } = useModal();
  const [editingRow, setEditingRow] = useState(null);
  const [editingCostRow, setEditingCostRow] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [addMode, setAddMode] = useState(null); // null | 'lookup' | 'cost'
  const [lookupQuery, setLookupQuery] = useState('');
  const [lookupResults, setLookupResults] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupTotal, setLookupTotal] = useState(0);
  const [selectedResult, setSelectedResult] = useState(null);
  const [addQty, setAddQty] = useState(1);
  const [addUnit, setAddUnit] = useState('kg');
  const [addCost, setAddCost] = useState(0);
  const searchTimerRef = useRef(null);
  const [newItem, setNewItem] = useState({ name: '', quantity: 0, unit: 'unit', cost_per_unit: 0, cost_unit: 'USD/unit', cost_type: 'material' });
  const [costLookupRow, setCostLookupRow] = useState(null);
  const [costLookupQuery, setCostLookupQuery] = useState('');
  const [costLookupResults, setCostLookupResults] = useState([]);
  const [costLookupLoading, setCostLookupLoading] = useState(false);
  const costSearchTimerRef = useRef(null);

  // Add stable IDs for React keys
  const rows = useMemo(() =>
    lciInputs.map((input, idx) => ({ ...input, _id: `${processId}_${idx}` })),
    [lciInputs, processId]
  );

  // -- Handlers ---------------------------------------------------------------

  const handleQuantityChange = (rowId, newQty) => {
    const updated = rows.map(r =>
      r._id === rowId ? { ...r, quantity: parseFloat(newQty) || 0 } : r
    );
    notifyParent(updated);
  };

  const handleCostChange = (rowId, newCost) => {
    const cost = parseFloat(newCost) || 0;
    // A typed price replaces the old one's citation rather than inheriting it.
    const updated = rows.map(r =>
      r._id === rowId && cost !== r.cost_per_unit ? withCost(r, cost) : r
    );
    notifyParent(updated);
  };

  const handleCostLookupSearch = useCallback(async (query, rowId) => {
    setCostLookupQuery(query);
    setCostLookupRow(rowId);
    if (costSearchTimerRef.current) clearTimeout(costSearchTimerRef.current);
    if (!query || query.trim().length < 1) {
      setCostLookupResults([]);
      setCostLookupLoading(false);
      return;
    }
    setCostLookupLoading(true);
    costSearchTimerRef.current = setTimeout(async () => {
      try {
        const resp = await window.electronAPI.invoke('search-cost-database', query, 10);
        if (resp.success) setCostLookupResults(resp.results);
        else setCostLookupResults([]);
      } catch (err) {
        console.error('[Cost Lookup] Search error:', err);
        setCostLookupResults([]);
      } finally {
        setCostLookupLoading(false);
      }
    }, 200);
  }, []);

  const handleApplyCostLookup = (rowId, costEntry) => {
    const updated = rows.map(r =>
      r._id === rowId ? withCost(r, costEntry.price_per_unit, {
        cost_unit: `USD/${costEntry.unit}`,
        // Carry the provenance with the number. `cost_confidence` is what lets
        // the TEA say how much of a total rests on uncited values.
        cost_ref: costEntry.id || '',
        cost_source: costEntry.source || '',
        cost_year: costEntry.year || '',
        cost_confidence: costEntry.confidence || 'unsourced',
      }) : r
    );
    notifyParent(updated);
    setCostLookupRow(null);
    setCostLookupQuery('');
    setCostLookupResults([]);
  };

  const handleDelete = async (rowId) => {
    const ok = await showConfirm('Delete this LCI input?', { title: 'Delete Input', type: 'warning' });
    if (!ok) return;
    notifyParent(rows.filter(r => r._id !== rowId));
  };

  const handleAddCostItem = () => {
    if (!newItem.name.trim()) return;
    const item = {
      name: newItem.name.trim(),
      quantity: parseFloat(newItem.quantity) || 1,
      unit: newItem.unit || 'unit',
      cost_per_unit: parseFloat(newItem.cost_per_unit) || 0,
      cost_unit: newItem.cost_unit || 'USD/unit',
      cost_type: newItem.cost_type || 'material',
    };
    notifyParent([...rows.map(({ _id, ...rest }) => rest), item]);
    setNewItem({ name: '', quantity: 0, unit: 'unit', cost_per_unit: 0, cost_unit: 'USD/unit', cost_type: 'material' });
    setAddMode(null);
  };

  // -- Lookup search ----------------------------------------------------------

  const handleLookupSearch = useCallback(async (query) => {
    setLookupQuery(query);
    setSelectedResult(null);

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    if (!query || query.trim().length < 2) {
      setLookupResults([]);
      setLookupLoading(false);
      return;
    }

    setLookupLoading(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const resp = await window.electronAPI.invoke('search-traci-lookup', query, 50, useNoisyDb, impactSource);
        if (resp.success) {
          setLookupResults(resp.results);
          setLookupTotal(resp.total);
        } else {
          setLookupResults([]);
        }
      } catch (err) {
        console.error('[Lookup] Search error:', err);
        setLookupResults([]);
      } finally {
        setLookupLoading(false);
      }
    }, 300); // 300ms debounce
  }, [useNoisyDb, impactSource]);

  const handleAddLookupInput = () => {
    if (!selectedResult) return;
    const resolvedUnit = addUnit || selectedResult.reference_product_unit || 'kg';
    // Linked under the dataset the result came from, never relabelled as another.
    const item = setImpactLink({
      name: selectedResult.reference_product || selectedResult.activity_name,
      reference_product_unit: selectedResult.reference_product_unit || '',
      quantity: parseFloat(addQty) || 1,
      unit: resolvedUnit,
      cost_per_unit: parseFloat(addCost) || 0,
      cost_unit: `USD/${resolvedUnit}`,
    }, impactSource, {
      scores: selectedResult.traci_scores,
      activity_name: selectedResult.activity_name,
      reference_product: selectedResult.reference_product,
      location: selectedResult.location,
    });
    // The lookup's citation belongs to its price, not to one the user typed over it.
    if (selectedResult.cost_source && item.cost_per_unit === selectedResult.cost_per_unit) item.cost_source = selectedResult.cost_source;
    notifyParent([...rows.map(({ _id, ...rest }) => rest), item]);
    // Reset
    setSelectedResult(null);
    setLookupQuery('');
    setLookupResults([]);
    setAddQty(1);
    setAddUnit('kg');
    setAddCost(0);
    setAddMode(null);
  };

  const notifyParent = (updated) => {
    if (!onInputsChange) return;
    // Strip internal _id before sending upstream
    onInputsChange(updated.map(({ _id, ...rest }) => rest));
  };

  // -- Helpers ----------------------------------------------------------------

  const hasScores = (input) => hasImpactScores(input, impactSource);

  const hasCost = (input) =>
    input.cost_per_unit !== undefined && input.cost_per_unit !== null;

  const formatNumber = (n) => {
    if (n === undefined || n === null) return '—';
    if (Math.abs(n) < 0.001 && n !== 0) return n.toExponential(2);
    if (Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
    return Number(n.toFixed(4)).toString();
  };

  // Count how many inputs have baked-in data
  const withScores = rows.filter(hasScores).length;
  // An ingredient of a part priced whole (cost_in) is covered by that part's row.
  const pricedCount = rows.filter(r => hasCost(r) || r.cost_in).length;

  // -- Render -----------------------------------------------------------------

  // Reusable add-buttons toolbar
  const renderAddToolbar = () => (
    <div style={{
      display: 'flex', gap: '8px', alignItems: 'center', padding: '8px 8px', flexWrap: 'wrap',
      background: '#f8f9fa', borderBottom: '1px solid #e8eaed', borderRadius: '4px 4px 0 0',
    }}>
      <button
        onClick={() => setAddMode(addMode === 'lookup' ? null : 'lookup')}
        style={{
          padding: '5px 14px', background: addMode === 'lookup' ? '#2e7d32' : '#fff',
          color: addMode === 'lookup' ? 'white' : '#2e7d32',
          border: '1px solid #2e7d32', borderRadius: 4, fontSize: '12px', whiteSpace: 'nowrap',
          cursor: 'pointer', transition: 'all 0.15s', fontWeight: 500,
        }}
      >
        + Add Input
      </button>
      {/* Which dataset the lookup searches: the project's active dataset, switchable here. */}
      <select
        value={impactSource}
        onChange={(e) => {
          if (onImpactSourceChange) onImpactSourceChange(e.target.value);
          setLookupResults([]); setSelectedResult(null);
          if (lookupQuery.length >= 2) handleLookupSearch(lookupQuery);
        }}
        disabled={!onImpactSourceChange}
        title={`${impactSourceLookupNote(impactSource)} Changing this switches the whole project's impact dataset.`}
        aria-label="Impact dataset to search"
        style={{
          padding: '5px 6px', fontSize: '12px', color: '#2e7d32', background: '#fff',
          border: '1px solid #c8e6c9', borderRadius: 4, cursor: onImpactSourceChange ? 'pointer' : 'default', maxWidth: '190px',
        }}
      >
        {IMPACT_SOURCE_ORDER.map((s) => (
          <option key={s} value={s}>{impactSourceChoiceLabel(s)}</option>
        ))}
      </select>
      <button
        onClick={() => setAddMode(addMode === 'cost' ? null : 'cost')}
        style={{
          padding: '5px 14px', background: addMode === 'cost' ? '#e65100' : '#fff',
          color: addMode === 'cost' ? 'white' : '#e65100',
          border: '1px solid #e65100', borderRadius: 4, fontSize: '12px', whiteSpace: 'nowrap',
          cursor: 'pointer', transition: 'all 0.15s', fontWeight: 500,
        }}
      >
        + Cost-Only Item
      </button>
      <div style={{ flex: 1 }} />
      <span style={{ fontSize: '11px', color: '#888' }}>
        {rows.length} input{rows.length !== 1 ? 's' : ''}
        {withScores > 0 && <span style={{ color: '#2e7d32', marginLeft: 8 }}>✓ {withScores} TRACI</span>}
        {pricedCount > 0 && <span style={{ color: '#1565c0', marginLeft: 8 }}>✓ {pricedCount} cost</span>}
      </span>
    </div>
  );

  // Impact-dataset lookup panel (ecoinvent or GREET, per the project's selection)
  const renderLookupPanel = () => (
    <div style={{
      padding: '10px 12px', background: '#f0f7ff', borderBottom: '1px solid #d0e3f7',
      fontSize: '12px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <div style={{ fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: '6px' }}>
          Add Input from {impactSourceLabel(impactSource)} Lookup
          <SourcesButton
            title={impactSourceLabel(impactSource)}
            sources={[IMPACT_SOURCE_CITATIONS[impactSource]].filter(Boolean)}
            notes={[impactSourceLookupNote(impactSource)]}
          />
        </div>
        <button
          onClick={() => { setAddMode(null); setLookupQuery(''); setLookupResults([]); setSelectedResult(null); }}
          style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '16px', lineHeight: 1 }}
        >&times;</button>
      </div>
      <input
        type="text"
        placeholder={`Search ${impactSourceLabel(impactSource)} activities (e.g. steel, lithium)...`}
        value={lookupQuery}
        onChange={e => handleLookupSearch(e.target.value)}
        autoFocus
        style={{
          width: '100%', padding: '6px 10px', border: '1px solid #ced4da',
          borderRadius: 4, fontSize: '13px', boxSizing: 'border-box',
        }}
      />
      {lookupLoading && (
        <div style={{ padding: '8px 0', color: '#666', fontSize: '12px' }}>Searching...</div>
      )}
      {!lookupLoading && lookupResults.length > 0 && !selectedResult && (
        <div style={{
          maxHeight: '200px', overflowY: 'auto', marginTop: '6px',
          border: '1px solid #e0e0e0', borderRadius: 4, background: '#fff',
        }}>
          {lookupResults.map((r, idx) => (
            <div
              key={idx}
              onClick={() => { setSelectedResult(r); if (r.reference_product_unit) setAddUnit(r.reference_product_unit); if (r.cost_per_unit != null) setAddCost(r.cost_per_unit); }}
              style={{
                padding: '6px 10px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#e8f0fe'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
            >
              <div style={{ fontWeight: 500, color: '#333', fontSize: '12px' }}>
                {r.reference_product}
              </div>
              <div style={{ color: '#666', fontSize: '11px' }}>
                {r.activity_name} — <span style={{ color: '#999' }}>{r.location}</span>
              </div>
            </div>
          ))}
          {lookupResults.length >= 50 && (
            <div style={{ padding: '6px 10px', color: '#999', fontSize: '11px', fontStyle: 'italic' }}>
              Showing first 50 of many matches — refine your search
            </div>
          )}
        </div>
      )}
      {!lookupLoading && lookupQuery.length >= 2 && lookupResults.length === 0 && !selectedResult && (
        <div style={{ padding: '8px 0', color: '#999', fontSize: '12px' }}>
          No matches found. Try different keywords.
          {impactSource === GREET_SOURCE && (
            <span> GREET 2025 has no entry for many specialty materials (carbon black, CMC, water, gas heat). If this is one, add it as a cost-only item: it will count in the TEA and as zero impact, and the Results page will list it as not covered.</span>
          )}
        </div>
      )}
      {selectedResult && (
        <div style={{ marginTop: '8px', padding: '10px', background: '#fff', border: '1px solid #c8e1ff', borderRadius: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 600, color: '#333', fontSize: '13px' }}>
                {selectedResult.reference_product}
              </div>
              <div style={{ color: '#666', fontSize: '11px', marginTop: '2px' }}>
                {selectedResult.activity_name} — {selectedResult.location}
              </div>
            </div>
            <button
              onClick={() => setSelectedResult(null)}
              style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '14px' }}
            >change</button>
          </div>
          <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: '1fr auto', gap: '1px 10px', fontSize: '11px' }}>
            {Object.entries(selectedResult.traci_scores).slice(0, 4).map(([cat, val]) => (
              <React.Fragment key={cat}>
                <span style={{ color: '#666' }}>{cat.replace(/_/g, ' ')}</span>
                {isCategoryCovered(impactSource, cat)
                  ? <span style={{ fontFamily: 'monospace', textAlign: 'right' }}>{formatNumber(val)}</span>
                  : <span style={{ color: '#999', fontStyle: 'italic', textAlign: 'right' }}>not covered</span>}
              </React.Fragment>
            ))}
            <span style={{ color: '#999', fontStyle: 'italic' }}>+ {Object.keys(selectedResult.traci_scores).length - 4} more categories</span>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '10px' }}>
            <label style={{ color: '#555', fontSize: '12px' }}>Qty:</label>
            <input type="number" value={addQty} onChange={e => setAddQty(e.target.value)}
              style={{ width: '70px', padding: '4px 6px', border: '1px solid #ced4da', borderRadius: 4, fontSize: '12px', textAlign: 'right' }} />
            <label style={{ color: '#555', fontSize: '12px' }}>Unit:</label>
            <input type="text" value={addUnit} onChange={e => setAddUnit(e.target.value)}
              style={{ width: '50px', padding: '4px 6px', border: '1px solid #ced4da', borderRadius: 4, fontSize: '12px' }} />
            <label style={{ color: '#555', fontSize: '12px' }}>Cost:</label>
            <span style={{ color: '#1565c0', fontSize: '12px' }}>$</span>
            <input type="number" value={addCost} onChange={e => setAddCost(e.target.value)}
              style={{ width: '60px', padding: '4px 6px', border: '1px solid #ced4da', borderRadius: 4, fontSize: '12px', textAlign: 'right' }} />
            <span style={{ color: '#888', fontSize: '11px' }}>/{addUnit}</span>
            {selectedResult.cost_source && <SourcesButton title="Price source" sources={[{ cite: selectedResult.cost_source }]} />}
            <div style={{ flex: 1 }} />
            <button onClick={handleAddLookupInput}
              style={{ padding: '5px 16px', background: '#2e7d32', color: 'white', border: 'none', borderRadius: 4, fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}
            >Add Input</button>
          </div>
        </div>
      )}
    </div>
  );

  // Cost-only item panel
  const renderCostPanel = () => (
    <div style={{
      padding: '10px 12px', background: '#fff8e1', borderBottom: '1px solid #ffe082',
      fontSize: '12px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <div style={{ fontWeight: 600, color: '#333' }}>Add Cost-Only Item (no LCA impact)</div>
        <button onClick={() => setAddMode(null)}
          style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '16px', lineHeight: 1 }}
        >&times;</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px', alignItems: 'center' }}>
        <input type="text" placeholder="Name (e.g. Equipment lease, Transport, Labor)"
          value={newItem.name} onChange={e => setNewItem({ ...newItem, name: e.target.value })} autoFocus
          style={{ padding: '4px 8px', border: '1px solid #ced4da', borderRadius: 4, fontSize: '12px' }} />
        <select value={newItem.cost_type} onChange={e => setNewItem({ ...newItem, cost_type: e.target.value })}
          style={{ padding: '4px 6px', border: '1px solid #ced4da', borderRadius: 4, fontSize: '12px' }}>
          <option value="material">Material</option>
          <option value="equipment">Equipment</option>
          <option value="transport">Transport</option>
          <option value="labor">Labor</option>
          <option value="energy">Energy</option>
          <option value="other">Other</option>
        </select>
      </div>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '6px' }}>
        <input type="number" placeholder="Qty" value={newItem.quantity || ''}
          onChange={e => setNewItem({ ...newItem, quantity: e.target.value })}
          style={{ width: '60px', padding: '4px 6px', border: '1px solid #ced4da', borderRadius: 4, fontSize: '12px', textAlign: 'right' }} />
        <input type="text" placeholder="Unit" value={newItem.unit}
          onChange={e => setNewItem({ ...newItem, unit: e.target.value })}
          style={{ width: '60px', padding: '4px 6px', border: '1px solid #ced4da', borderRadius: 4, fontSize: '12px' }} />
        <span style={{ color: '#666' }}>@</span>
        <span style={{ color: '#1565c0', fontSize: '12px' }}>$</span>
        <input type="number" placeholder="$/unit" value={newItem.cost_per_unit || ''}
          onChange={e => setNewItem({ ...newItem, cost_per_unit: e.target.value })}
          style={{ width: '70px', padding: '4px 6px', border: '1px solid #ced4da', borderRadius: 4, fontSize: '12px', textAlign: 'right' }} />
        <div style={{ flex: 1 }} />
        <button onClick={handleAddCostItem} disabled={!newItem.name.trim()}
          style={{ padding: '4px 12px', background: '#e65100', color: 'white', border: 'none', borderRadius: 4, fontSize: '12px', cursor: 'pointer', opacity: newItem.name.trim() ? 1 : 0.5 }}
        >Add</button>
        <button onClick={() => setAddMode(null)}
          style={{ padding: '4px 12px', background: '#e9ecef', color: '#333', border: 'none', borderRadius: 4, fontSize: '12px', cursor: 'pointer' }}
        >Cancel</button>
      </div>
    </div>
  );

  if (rows.length === 0) {
    return (
      <div className="lci-inputs-container" style={{ padding: 0, background: 'transparent', marginTop: 0 }}>
        {renderAddToolbar()}
        {addMode === 'lookup' && renderLookupPanel()}
        {addMode === 'cost' && renderCostPanel()}
        {!addMode && (
          <div style={{ padding: '20px 12px', color: '#999', fontSize: '13px', textAlign: 'center' }}>
            No LCI inputs defined for this process.<br />
            <span style={{ fontSize: '12px' }}>Use the buttons above to add material, energy, or cost inputs.</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="lci-inputs-container" style={{ padding: 0, background: 'transparent', marginTop: 0 }}>
      {renderAddToolbar()}
      {rows.map(input => {
        const isEditing = editingRow === input._id;
        const isExpanded = expandedRow === input._id;
        const scores = getImpactScores(input, impactSource);
        const link = getImpactLink(input, impactSource);
        const qty = input.quantity || 0;

        return (
          <React.Fragment key={input._id}>
            <div
              onClick={() => setExpandedRow(isExpanded ? null : input._id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '6px 8px',
                borderBottom: '1px solid #f0f0f0',
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              <span style={{ color: '#333', flex: 1 }}>
                {input.name || 'Unnamed'}
                {isCostOnly(input) && (
                  <span style={{ color: '#999', fontSize: '11px', marginLeft: 6 }} title="No carbon: a price only">price only</span>
                )}
              </span>
              <span
                onClick={e => e.stopPropagation()}
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                {isEditing ? (
                  <input
                    type="number"
                    value={input.quantity}
                    onChange={e => handleQuantityChange(input._id, e.target.value)}
                    onBlur={() => setEditingRow(null)}
                    onKeyDown={e => e.key === 'Enter' && setEditingRow(null)}
                    autoFocus
                    style={{
                      width: '70px', padding: '2px 6px', border: '1px solid #1a73e8',
                      borderRadius: 4, fontSize: '13px', textAlign: 'right',
                    }}
                  />
                ) : (
                  <span
                    onClick={() => setEditingRow(input._id)}
                    title="Click to edit"
                    style={{
                      background: '#fff', border: '1px solid #e0e0e0', borderRadius: 4,
                      padding: '2px 8px', fontSize: '13px', cursor: 'pointer',
                      minWidth: '50px', textAlign: 'right', display: 'inline-block',
                    }}
                  >
                    {formatNumber(input.quantity)}
                  </span>
                )}
                <span style={{ color: '#888', fontSize: '12px', minWidth: '30px' }}>{input.unit || ''}</span>
                {editingCostRow === input._id ? (
                  <span onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <span style={{ color: '#1565c0', fontSize: '12px' }}>$</span>
                    <input
                      type="number"
                      value={input.cost_per_unit ?? 0}
                      onChange={e => handleCostChange(input._id, e.target.value)}
                      onBlur={() => setEditingCostRow(null)}
                      onKeyDown={e => e.key === 'Enter' && setEditingCostRow(null)}
                      autoFocus
                      style={{
                        width: '60px', padding: '2px 4px', border: '1px solid #1565c0',
                        borderRadius: 4, fontSize: '12px', textAlign: 'right',
                      }}
                    />
                    <span style={{ color: '#888', fontSize: '11px' }}>/{input.unit}</span>
                  </span>
                ) : (
                  <span
                    onClick={e => { e.stopPropagation(); setEditingCostRow(input._id); }}
                    title={!hasCost(input) && input.cost_in ? `Priced in ${input.cost_in}` : 'Click to edit cost'}
                    style={{
                      color: !hasCost(input) && input.cost_in ? '#999' : '#1565c0',
                      fontSize: '12px', minWidth: '60px', textAlign: 'right', cursor: 'pointer',
                    }}
                  >
                    {hasCost(input) ? `$${formatNumber(input.cost_per_unit)}/${input.unit}` : input.cost_in ? 'in part price' : '+ cost'}
                  </span>
                )}
                {input.cost_source && <SourcesButton title="Price source" {...priceSources(input)} />}
                <span
                  onClick={(e) => { e.stopPropagation(); handleDelete(input._id); }}
                  style={{
                    color: '#bbb', fontSize: '16px', cursor: 'pointer', marginLeft: '4px',
                    lineHeight: 1, fontWeight: 300, transition: 'color 0.15s',
                  }}
                  onMouseEnter={e => e.target.style.color = '#dc3545'}
                  onMouseLeave={e => e.target.style.color = '#bbb'}
                  title="Delete input"
                >&times;</span>
              </span>
            </div>

            {/* Expanded detail row */}
            {isExpanded && (
              <div style={{ padding: '8px 12px', background: '#f8f9fa', fontSize: '12px', borderBottom: '1px solid #e0e0e0' }}>
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                  {/* Source info */}
                  <div style={{ minWidth: '200px' }}>
                    <div style={{ fontWeight: 600, marginBottom: '4px', color: '#333' }}>Source · {impactSourceLabel(impactSource)}</div>
                    {link ? (
                      <>
                        {link.activity_name && <div><span style={{ color: '#666' }}>Activity:</span> {link.activity_name}</div>}
                        {link.reference_product && <div><span style={{ color: '#666' }}>Product:</span> {link.reference_product}</div>}
                        {link.location && <div><span style={{ color: '#666' }}>Location:</span> {link.location}</div>}
                        {link.note && <div style={{ color: '#b26a00' }}>{link.note}</div>}
                      </>
                    ) : (
                      <div style={{ color: '#b26a00' }}>
                        No {impactSourceLabel(impactSource)} equivalent — counts as zero in results.
                      </div>
                    )}
                  </div>

                  {/* TRACI scores */}
                  {hasScores(input) && (
                    <div style={{ minWidth: '280px' }}>
                      <div style={{ fontWeight: 600, marginBottom: '4px', color: '#333' }}>
                        TRACI v2.1 Scores (per {input.unit})
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '2px 12px' }}>
                        {Object.entries(scores).map(([cat, val]) => (
                          <React.Fragment key={cat}>
                            <span style={{ color: '#666' }}>{cat.replace(/_/g, ' ')}</span>
                            {isCategoryCovered(impactSource, cat)
                              ? <span style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatNumber(val)}</span>
                              : <span style={{ textAlign: 'right', color: '#999', fontStyle: 'italic' }}>not covered</span>}
                          </React.Fragment>
                        ))}
                      </div>
                      {qty > 0 && (
                        <div style={{ marginTop: '6px', borderTop: '1px solid #e0e0e0', paddingTop: '4px' }}>
                          <div style={{ fontWeight: 600, color: '#333' }}>Total Impact (qty × score)</div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '2px 12px' }}>
                            {Object.entries(scores).map(([cat, val]) => (
                              <React.Fragment key={cat}>
                                <span style={{ color: '#666' }}>{cat.replace(/_/g, ' ')}</span>
                                {isCategoryCovered(impactSource, cat)
                                  ? <span style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{formatNumber(qty * val)}</span>
                                  : <span style={{ textAlign: 'right', color: '#999', fontStyle: 'italic' }}>not covered</span>}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Cost + DB Lookup */}
                  <div style={{ minWidth: '180px' }}>
                    <div style={{ fontWeight: 600, marginBottom: '4px', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Cost
                      <button
                        onClick={(e) => { e.stopPropagation(); setCostLookupRow(costLookupRow === input._id ? null : input._id); setCostLookupQuery(input.name || ''); handleCostLookupSearch(input.name || '', input._id); }}
                        style={{
                          padding: '1px 8px', fontSize: '10px', background: '#e3f2fd', color: '#1565c0',
                          border: '1px solid #90caf9', borderRadius: 3, cursor: 'pointer', fontWeight: 500,
                        }}
                      >{costLookupRow === input._id ? 'Close' : 'DB Lookup'}</button>
                    </div>
                    {hasCost(input) && (
                      <>
                        <div><span style={{ color: '#666' }}>Per unit:</span> ${formatNumber(input.cost_per_unit)} {input.cost_unit || ''}</div>
                        {qty > 0 && (
                          <div style={{ fontWeight: 600, marginTop: '4px' }}>
                            Total: ${formatNumber(qty * input.cost_per_unit)}
                          </div>
                        )}
                        {!input.cost_source && input.cost_confidence === 'unsourced' && (
                          <div style={{ marginTop: '4px', fontSize: '10px', color: '#b26a00', fontStyle: 'italic' }}>
                            No cost citation
                          </div>
                        )}
                      </>
                    )}
                    {costLookupRow === input._id && (
                      <div style={{ marginTop: '6px', padding: '6px', background: '#fff', border: '1px solid #90caf9', borderRadius: 4 }} onClick={e => e.stopPropagation()}>
                        <input
                          type="text"
                          placeholder="Search cost database..."
                          value={costLookupQuery}
                          onChange={e => handleCostLookupSearch(e.target.value, input._id)}
                          autoFocus
                          style={{ width: '100%', padding: '4px 6px', border: '1px solid #ced4da', borderRadius: 3, fontSize: '11px', boxSizing: 'border-box' }}
                        />
                        {costLookupLoading && <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>Searching...</div>}
                        {!costLookupLoading && costLookupResults.length > 0 && (
                          <div style={{ maxHeight: '120px', overflowY: 'auto', marginTop: '4px' }}>
                            {costLookupResults.map((entry, idx) => (
                              <div
                                key={idx}
                                onClick={() => handleApplyCostLookup(input._id, entry)}
                                style={{
                                  padding: '3px 6px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
                                  fontSize: '11px', transition: 'background 0.1s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#e8f0fe'}
                                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                              >
                                <span style={{ fontWeight: 500 }}>{entry.item}</span>
                                <span style={{ color: '#1565c0', marginLeft: '6px' }}>${entry.price_per_unit}/{entry.unit}</span>
                                <div style={{ color: entry.source ? '#2e7d32' : '#b26a00', fontSize: '10px' }}>
                                  {entry.source ? `${entry.source}` : 'no citation recorded'}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {!costLookupLoading && costLookupQuery.length >= 1 && costLookupResults.length === 0 && (
                          <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>No matches</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </React.Fragment>
        );
      })}

      {/* ── Add panels (shown below inputs list) ── */}
      {addMode === 'lookup' && renderLookupPanel()}
      {addMode === 'cost' && renderCostPanel()}
    </div>
  );
};

export default LCIInputsTable;
